import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { FCM_CHANNEL_ID } from '../services/notificationService';

interface PushNotificationOptions {
  /** Åpnes varslingspanelet i appen */
  onOpenNotifications: () => void;
  /** Navigerer til en kollaborativ kart-lenke */
  onOpenCollabMap?: (mapId: string) => void;
  /** Navigerer til en spesifikk plass. opts.verify åpner den klar for verifisering. */
  onGoToSpot?: (spotId: string, opts?: { verify?: boolean }) => void;
  /** Åpner hurtiggodkjenning for et artikkelutkast (kun admin får dette varselet). */
  onOpenArticleReview?: (articleId: string) => void;
  /** Er brukeren pålogget? */
  isAuthenticated: boolean;
}

/**
 * Initialiserer Capacitor PushNotifications-lyttere på native Android/iOS.
 *
 * Håndterer:
 *  - Kanalopprettelse ved oppstart (Android 8+)
 *  - Forgrunns-varsler (vises ikke automatisk på Android — oppdaterer UI-badge)
 *  - Tap på varsling fra bakgrunnen/lukket tilstand (deep link)
 */
export function usePushNotificationListeners({
  onOpenNotifications,
  onOpenCollabMap,
  onGoToSpot,
  onOpenArticleReview,
  isAuthenticated,
}: PushNotificationOptions): void {
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !isAuthenticated) return;

    let mounted = true;

    const setup = async () => {
      // Sørg for at kanalen finnes ved oppstart (idempotent)
      try {
        await PushNotifications.createChannel({
          id: FCM_CHANNEL_ID,
          name: 'Fricamp-varsler',
          description: 'Varsler fra Fricamp om plasser, invitasjoner og aktivitet',
          importance: 4,
          sound: 'default',
          vibration: true,
          visibility: 1,
        });
      } catch {
        // Kanalen kan allerede finnes — ignorer
      }

      if (!mounted) return;

      // ── Forgrunns-varsel ─────────────────────────────────────────────────
      // Android viser IKKE system-varsler automatisk når appen er aktiv.
      // Firestore-lytteren i useFirebaseSync fanger opp endringen og
      // oppdaterer badge-teller, så her trenger vi ikke gjøre mer.
      await PushNotifications.addListener('pushNotificationReceived', (_notification) => {
        // Badge-telleren oppdateres via Firestore-lyttere.
        // Legg til toast-logikk her dersom ønskelig i fremtiden.
      });

      // ── Tap på varsling (bakgrunn / lukket app) ──────────────────────────
      await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        if (!mounted) return;
        const data = action.notification.data ?? {};
        const type = data.type as string | undefined;

        if ((type === 'collab_invite' || type === 'collab_message') && data.mapId && onOpenCollabMap) {
          onOpenCollabMap(data.mapId as string);
        } else if (type === 'article_review' && data.articleId && onOpenArticleReview) {
          // Hurtiggodkjenning: rett inn i ett-trykks-modalen, ikke til varsellista.
          onOpenArticleReview(data.articleId as string);
        } else if ((type === 'new_spot' || type === 'county_new_spot' || type === 'new_review' || type === 'county_new_review' || type === 'verify_nudge') && data.spotId && onGoToSpot) {
          // verify_nudge → åpne plassen klar for verifisering (ett-tapps).
          onGoToSpot(data.spotId as string, { verify: type === 'verify_nudge' });
        } else {
          // Alle andre varsler: åpne varslingspanelet
          onOpenNotifications();
        }
      });
    };

    setup();

    return () => {
      mounted = false;
      PushNotifications.removeAllListeners().catch(() => {});
    };
  }, [isAuthenticated, onOpenNotifications, onOpenCollabMap, onGoToSpot, onOpenArticleReview]);
}
